import React, { useEffect, useState } from "react";

import { CloseIcon, InfoIcon } from "../../lib/icons";

const ROLE_ORDER = ["agent", "manager", "admin"];

const ROLE_GUIDES = {
  agent: {
    flowTitle: "Agent daily flow",
    flowDescription: "Use this sequence to move assigned leads forward without missing callbacks.",
    cards: [
      {
        title: "What agents should do first",
        items: [
          "Open `My Leads` and review the next follow-up queue before starting fresh calls.",
          "Work only leads visible in your assigned geography and ownership scope.",
          "Open the lead journey when you need previous call notes or uploaded documents.",
          "Keep phone number, city, and follow-up timing clean so manager reports stay accurate.",
        ],
      },
      {
        title: "How to handle calls",
        items: [
          "Use the call action to start the Ozonetel dial from the lead list or follow-up board.",
          "After the conversation ends, complete the same call log with status, disposition, and remarks.",
          "If a sub-disposition requires follow-up, add the next callback date before saving.",
          "Use remarks for concise outcome notes, objections, budget, or commitment details.",
        ],
      },
      {
        title: "How follow-ups work",
        items: [
          "Callback tasks appear in `Follow-ups` until marked completed or replaced by a newer call outcome.",
          "Use `Log Call` from the card when the customer answers or when a retry is attempted.",
          "Mark the card complete only when the task itself is closed, not just because time passed.",
          "Follow-up completion directly affects pending workload and call reporting.",
        ],
      },
      {
        title: "Data quality rules",
        items: [
          "Choose the correct parent disposition first, then the exact sub-disposition.",
          "Do not leave city or region ambiguous if the lead is already known.",
          "Use uploaded documents only for lead-specific files that belong in the CRM journey.",
          "Avoid duplicate manual lead creation when an existing lead already appears in the duplicate check.",
        ],
      },
    ],
    steps: [
      { title: "1. Review Queue", description: "Check assigned leads and pending follow-ups." },
      { title: "2. Start Call", description: "Trigger Ozonetel and speak to the client." },
      { title: "3. Save Outcome", description: "Log disposition, remarks, and callback date." },
      { title: "4. Move Next", description: "Work the next visible lead with clean status updates." },
    ],
    title: "How agents should work in Indofast",
  },
  manager: {
    flowTitle: "Manager operating flow",
    flowDescription: "Use this sequence to keep the team queue, imports, and geography-scoped reporting aligned.",
    cards: [
      {
        title: "Lead distribution and coverage",
        items: [
          "Review visible leads, follow-ups, and agent workload inside your geography scope.",
          "Use assignment changes carefully because assignment history feeds reporting and audit trails.",
          "Check `My Agents` to understand active agent coverage before importing or redistributing leads.",
          "Keep region and city mappings consistent when reviewing team-created records.",
        ],
      },
      {
        title: "Bulk upload and reassignment",
        items: [
          "Use `Bulk Upload` for CSV/XLSX lead imports when leads are coming from files.",
          "Use `Bulk Assign` for reassignment files keyed by client ID or phone plus agent.",
          "Review invalid rows and download the invalid file before attempting the next upload.",
          "Each successful upload or reassignment is written into the CRM audit history automatically.",
        ],
      },
      {
        title: "Facebook lead sync",
        items: [
          "Use `Sync Facebook Leads` to pull recent leads directly from the configured Facebook page.",
          "If the sync fails, review the surfaced missing settings before retrying.",
          "Treat manual Meta `leadgen_id` fetches as a developer debug path, not a manager workflow.",
          "Refresh reports after imports when you need to verify geography and source attribution.",
        ],
      },
      {
        title: "How managers should read reports",
        items: [
          "`City Wise` groups by city and shows the linked region for the current scope.",
          "`Region Wise` rolls up totals at the region level and respects active filters.",
          "Agent visibility is controlled by mapped geography access, not plain text name matching.",
          "Missing city or region values appear as `Unspecified`, so fix source data early.",
        ],
      },
    ],
    steps: [
      { title: "1. Review Team Scope", description: "Check agents, queue health, and open callbacks." },
      { title: "2. Import or Reassign", description: "Use bulk tools or Facebook sync as needed." },
      { title: "3. Monitor Outcomes", description: "Confirm agents are logging clean call dispositions." },
      { title: "4. Read Reports", description: "Track performance by source, city, region, and agent." },
    ],
    title: "How managers should run Indofast",
  },
  admin: {
    flowTitle: "Admin control flow",
    flowDescription: "Use this sequence to keep users, geographies, and integrations configured for the whole CRM.",
    cards: [
      {
        title: "User and team setup",
        items: [
          "Create single users or bulk-create users from the admin settings workspace.",
          "Set the correct role at creation time so navigation and permissions line up with the backend scope.",
          "Keep teams current before assigning managers and agents operationally.",
          "Use admin review to catch inactive or mis-scoped users before workflow issues spread downstream.",
        ],
      },
      {
        title: "Geography and visibility control",
        items: [
          "Create region, city, zone, and locality nodes from the settings panel as needed.",
          "Use assignments to map manager visibility to the correct geography nodes.",
          "String or UUID identifiers are handled directly by the UI, so preserve backend-generated IDs.",
          "Geography structure drives what managers can view and which agents they can manage.",
        ],
      },
      {
        title: "Integration diagnostics",
        items: [
          "Use the settings diagnostics panel to verify whether Facebook Leads and Ozonetel are configured.",
          "Review surfaced missing environment keys before investigating frontend behavior.",
          "Facebook sync and Ozonetel call start rely on backend configuration, not frontend-only toggles.",
          "Use refresh status after backend config changes to confirm the application sees the new setup.",
        ],
      },
      {
        title: "Admin audit expectations",
        items: [
          "Bulk uploads, reassignments, Facebook imports, and call activity all feed reporting and audit history.",
          "Treat disposition structure and geography naming as controlled system data.",
          "Use reports to validate operational quality, then use settings to correct root-cause configuration issues.",
          "Guide managers toward sync-based imports and agents toward clean call logging rather than manual workarounds.",
        ],
      },
    ],
    steps: [
      { title: "1. Configure Access", description: "Create users, teams, and manager scopes." },
      { title: "2. Maintain Master Data", description: "Keep geographies and assignments aligned." },
      { title: "3. Verify Integrations", description: "Check Facebook and Ozonetel diagnostics." },
      { title: "4. Support Operations", description: "Use reports and audit history to correct process gaps." },
    ],
    title: "How admins should manage Indofast",
  },
};

function normalizeGuideRole(role) {
  if (role === "admin") {
    return "admin";
  }
  if (role === "manager" || role === "senior_manager") {
    return "manager";
  }
  return "agent";
}

function visibleGuideRoles(role) {
  const normalizedRole = normalizeGuideRole(role);

  if (normalizedRole === "admin") {
    return ROLE_ORDER;
  }

  return [normalizedRole];
}

function WorkflowGuideModal({ currentUser, onClose, open }) {
  const defaultRole = normalizeGuideRole(currentUser?.role);
  const allowedRoles = visibleGuideRoles(currentUser?.role);
  const [activeRole, setActiveRole] = useState(defaultRole);

  // Only reset the active tab when the modal is freshly opened — not on
  // every tab click (which was causing the admin tab to always snap back).
  useEffect(() => {
    if (open) {
      setActiveRole(defaultRole);
    }
  }, [open]); // eslint-disable-line

  if (!open) {
    return null;
  }

  const guide = ROLE_GUIDES[activeRole];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel large" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="modal-kicker">Workflow Guide</p>
            <h2>{guide.title}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="workflow-guide-body">
          <section className="workflow-guide-card">
            <div className="workflow-guide-head">
              <InfoIcon />
              <div>
                <h3>{guide.flowTitle}</h3>
                <p>{guide.flowDescription}</p>
              </div>
            </div>

            {allowedRoles.length > 1 ? (
              <div className="report-tab-strip" style={{ marginBottom: "1.5rem" }}>
                {allowedRoles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={`report-tab-button ${activeRole === role ? "active" : ""}`}
                    onClick={() => setActiveRole(role)}
                  >
                    <span>{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                  </button>
                ))}
              </div>
            ) : null}

            <div className="workflow-flowchart">
              {guide.steps.map((step, index) => (
                <React.Fragment key={step.title}>
                  <div className="workflow-step">
                    <strong>{step.title}</strong>
                    <span>{step.description}</span>
                  </div>
                  {index < guide.steps.length - 1 ? <div className="workflow-arrow">→</div> : null}
                </React.Fragment>
              ))}
            </div>
          </section>

          <section className="workflow-guide-grid">
            {guide.cards.map((card) => (
              <article key={card.title} className="workflow-guide-card">
                <h3>{card.title}</h3>
                <ul className="workflow-list">
                  {card.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}

export default WorkflowGuideModal;
